import React, { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, InputAdornment, TextField, useMediaQuery } from '@mui/material';
import Search from '@mui/icons-material/Search';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DirectoryPagination from '../directories/DirectoryPagination';
import PlayerRankingResults from '../directories/PlayerRankingResults';
import { getJugadorEstaditicasByIdGrupoAndIdTemporada } from '../../services/jugadorEstadisticasService';
import { playerKey, playerMetric as teamPlayerMetric, playerName } from '../../utils/playerRanking';
import { playerComparisonUrl, playerDiscoveryUrl, playerPosition } from '../../utils/playerDiscovery';

const METRICS = ['edad', 'partidosJugados', 'titularidades', 'convocatorias', 'minutosJugados',
  'golesTotales', 'golesPenaltis', 'minutosPorGol', 'golesPorPartido',
  'amarillasTotales', 'segundasAmarillasTotales', 'rojasTotales', 'puntosDisciplinarios'];
export const TEAM_PLAYER_GROUPS = {
  participation: METRICS.slice(0, 5),
  goals: METRICS.slice(5, 9),
  discipline: METRICS.slice(9),
  all: METRICS,
};
const GROUPS = { summary: ['partidosJugados', 'minutosJugados', 'golesTotales', 'minutosPorGol'], ...TEAM_PLAYER_GROUPS };
export { teamPlayerMetric };
const normalize = value => String(value || '').normalize('NFD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase();

export const teamPlayersQuery = (team, navigate) => ({
  queryKey: ['team-statistics-players', team.idTemporada, team.idGrupo, team.idEquipo ?? team.id],
  queryFn: async () => {
    const data = await getJugadorEstaditicasByIdGrupoAndIdTemporada(team.idGrupo, team.idTemporada, navigate);
    if (!Array.isArray(data)) throw new Error('Invalid team player statistics');
    return data.filter(player => String(player.idEquipo) === String(team.idEquipo ?? team.id) &&
      (player.idTemporada == null || String(player.idTemporada) === String(team.idTemporada)) &&
      (player.idGrupo == null || String(player.idGrupo) === String(team.idGrupo)));
  },
  staleTime: 5 * 60 * 1000,
  retry: false,
});

export default function TeamStatisticsPlayers({ team }) {
  const { t, i18n } = useTranslation('stats');
  const { t: d } = useTranslation('directories');
  const navigate = useNavigate();
  const location = useLocation();
  const titleId = useId();
  const summaryRef = useRef();
  const mobile = useMediaQuery('(max-width:699px)');
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('summary');
  const [sort, setSort] = useState({ key: 'golesTotales', direction: 'desc' });
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const teamId = team?.idEquipo ?? team?.id;
  const players = useQuery({
    ...teamPlayersQuery(team || {}, navigate),
    enabled: Boolean(teamId != null && team?.idGrupo && team?.idTemporada),
  });
  const rows = (players.data || []).filter(player => normalize(playerName(player)).includes(normalize(search)))
    .slice().sort((a, b) => {
      const first = sort.key === 'nombre' ? playerName(a) : teamPlayerMetric(a, sort.key);
      const second = sort.key === 'nombre' ? playerName(b) : teamPlayerMetric(b, sort.key);
      const result = sort.key === 'nombre' ? first.localeCompare(second, i18n.language) : first - second;
      return (sort.direction === 'asc' ? 1 : -1) * result;
    });
  const pageSize = 25;
  const currentPage = Math.min(page, Math.max(1, Math.ceil(rows.length / pageSize)));
  const visible = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const selected = visible.find(player => playerKey(player) === selectedId);
  const visibleSelectedId = selected && playerKey(selected);
  useEffect(() => {
    if (mobile && visibleSelectedId) {
      summaryRef.current?.focus({ preventScroll: true });
      summaryRef.current?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  }, [mobile, visibleSelectedId]);
  const changeSort = key => setSort(previous => ({
    key, direction: previous.key === key && previous.direction === 'asc' ? 'desc' : 'asc',
  }));
  const metricLabel = key => key === 'partidosJugados' ? t('rankings:players.table.matchesPlayed')
    : key === 'minutosPorGol' ? t('rankings:players.table.minutesPerGoal') : t(`teams.modern.metrics.${key}`);
  const metrics = GROUPS[group].map(key => [key, metricLabel(key)]);
  const displayMetric = (player, key) => teamPlayerMetric(player, key).toLocaleString(i18n.language, {
    maximumFractionDigits: key === 'golesPorPartido' ? 2 : 1,
  });
  const profileUrl = player => player.id != null || player.idJugador != null
    ? `/jugador?${new URLSearchParams({
      ...(player.id != null ? { id: player.id } : { idJugador: player.idJugador }), idTemporada: team.idTemporada,
    })}` : null;
  const profileState = player => ({ jugador: player, directoryFrom: location.pathname + location.search });
  return <section className="ts-players dr-page dr-ranking" aria-labelledby={titleId}>
    <header className="ts-section-heading"><span className="rr-eyebrow">{t('teams.modern.squadEyebrow')}</span>
      <h2 id={titleId}>{t('teams.modern.playersTitle')}</h2><p>{t('teams.modern.playersSubtitle')}</p></header>
    <div className={`dr-layout${selected ? '' : ' ts-player-full'}`}><div className="rr-panel">
      <div className="ts-player-toolbar">
        <TextField label={t('teams.modern.searchPlayer')} value={search} onChange={event => {
          setSearch(event.target.value); setPage(1);
        }} InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
        {players.isSuccess && <span>{t('teams.modern.playersCount', { count: rows.length })}</span>}
      </div>
      <div className="ts-metric-groups" role="group" aria-label={t('teams.modern.metricGroups')}>
        {Object.keys(GROUPS).map(key => <button type="button" key={key} aria-pressed={group === key}
          onClick={() => { setGroup(key); setPage(1); if (!GROUPS[key].includes(sort.key)) setSort({ key: 'nombre', direction: 'asc' }); }}>
          {t(key === 'summary' ? 'teams.tabs.summary' : `teams.modern.groups.${key}`)}
        </button>)}
      </div>
      {players.isLoading && <p role="status" className="rr-empty">{t('teams.modern.loadingPlayers')}</p>}
      {players.isError && <Alert severity="error" action={<Button onClick={() => players.refetch()}>
        {t('teams.errors.retry')}</Button>}>{t('teams.modern.playersError')}</Alert>}
      {players.isSuccess && rows.length === 0 && <p className="rr-empty">{t('teams.modern.noPlayers')}</p>}
      {rows.length > 0 && <PlayerRankingResults players={visible} metrics={metrics}
        title={t('teams.modern.playersTable')} sort={sort.key} direction={sort.direction}
        onSort={changeSort} sortLabel={label => t('teams.modern.sortBy', { metric: label })}
        onSelect={setSelectedId} selectedId={visibleSelectedId} profileUrl={profileUrl} profileState={profileState}
        displayMetric={displayMetric} cards={mobile} offset={(currentPage - 1) * pageSize} idPrefix="team-player" />}
      <DirectoryPagination page={currentPage} pageSize={pageSize} total={rows.length} onChange={setPage} />
      <p className="ts-table-note">{t('teams.modern.playersDataNote')}</p>
    </div>
    {selected && <aside className="dr-panel dr-profile ts-player-summary" aria-label={d('selectedPlayer')}
      ref={summaryRef} tabIndex={-1}>
      <p className="dr-muted">{d('selectedPlayer')}</p><h2>{playerName(selected)}</h2>
      {playerPosition(selected) && <p>{playerPosition(selected)}</p>}
      <dl className="dr-kpis">{metrics.map(([key, label]) => <div key={key}>
        <dt>{label}</dt><dd>{displayMetric(selected, key)}</dd>
      </div>)}</dl>
      <div className="dr-profile-actions">
        {profileUrl(selected) && <Button component={Link} to={profileUrl(selected)} state={profileState(selected)}
          aria-label={d('profile')}>{d('discovery.profileShort')}</Button>}
        {playerComparisonUrl(selected) && <Button component={Link} to={playerComparisonUrl(selected)}>
          {d('comparePlayers')}</Button>}
        {playerDiscoveryUrl(selected) && <Button component={Link} to={playerDiscoveryUrl(selected)}>
          {d('discovery.similarAction')}</Button>}
        <Button onClick={() => setSelectedId(null)}>{d('close')}</Button>
      </div>
    </aside>}</div>
  </section>;
}
